/**
 * «دارایی‌ها و شبکه‌ها» (Phase 5; PRODUCT_REQUIREMENTS §8.1). Admin-managed catalogue — nothing here is
 * hard-coded product truth: listing + deposit/withdraw flags per asset, networks (validator family,
 * memo rule, explorer templates, flags, a temporary-unavailable label), versioned fee/minimum policy
 * (reference or explicit override; withdrawals snapshot it) and shared deposit addresses (validated,
 * immutable; retiring one reassigns its users). Every change: step-up + reason + audit.
 */
import {
  AppContainer,
  Badge,
  Button,
  Card,
  PageHeader,
  SelectField,
  StateView,
  TextField,
} from '@hopex/ui';
import { useState } from 'react';
import { api, dt } from '../../api.ts';
import { CommandDialog, Facts } from '../../lib/command.tsx';
import { exact } from '../../lib/crypto-model.ts';
import { Remote, useRemote } from '../../lib/remote.tsx';
import { LongId } from './shared.tsx';

interface Net {
  id: string;
  code: string;
  nameFa: string;
  nameEn: string;
  family: string;
  memoRequired: boolean;
  memoLabelFa: string | null;
  depositEnabled: boolean;
  withdrawEnabled: boolean;
  unavailableLabelFa: string | null;
  explorerTx: string | null;
  explorerAddress: string | null;
  sortOrder: number;
  version: string;
  fee: {
    fee: string;
    min: string;
    source: string;
    referenceProvider: string | null;
    createdAt: string;
  } | null;
  addresses: {
    id: string;
    address: string;
    label: string;
    status: string;
    assignedUsers: number;
    version: string;
  }[];
}
interface AssetRow {
  code: string;
  nameFa: string;
  nameEn: string;
  scale: number;
  listed: boolean;
  depositEnabled: boolean;
  withdrawEnabled: boolean;
  buyEnabled: boolean;
  sellEnabled: boolean;
  convertEnabled: boolean;
  isNew: boolean;
  sortOrder: number;
  unavailableLabelFa: string | null;
  version: string;
  networks: Net[];
}

type Open =
  | { k: 'asset'; a: AssetRow }
  | { k: 'net-new'; a: AssetRow }
  | { k: 'net'; a: AssetRow; n: Net }
  | { k: 'fee'; a: AssetRow; n: Net }
  | { k: 'addr'; a: AssetRow; n: Net }
  | { k: 'retire'; a: AssetRow; n: Net; id: string; version: string; address: string };

const Check = ({
  label,
  v,
  on,
  data,
}: {
  label: string;
  v: boolean;
  on: (b: boolean) => void;
  data?: string;
}) => (
  <label className="flex items-center gap-2 text-[13px]">
    <input
      type="checkbox"
      checked={v}
      onChange={(e) => on(e.target.checked)}
      {...(data ? { [`data-${data}`]: true } : {})}
    />
    {label}
  </label>
);

function AssetDialog({
  a,
  onClose,
  onDone,
}: {
  a: AssetRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [listed, setListed] = useState(a.listed);
  const [dep, setDep] = useState(a.depositEnabled);
  const [wd, setWd] = useState(a.withdrawEnabled);
  // Owner 2026-10-14: the trade routes were only settable at list import; now per asset.
  const [buy, setBuy] = useState(a.buyEnabled);
  const [sell, setSell] = useState(a.sellEnabled);
  const [conv, setConv] = useState(a.convertEnabled);
  const [isNew, setIsNew] = useState(a.isNew);
  const [label, setLabel] = useState(a.unavailableLabelFa ?? '');
  return (
    <CommandDialog
      title={`فهرست‌سازی ${a.nameFa} (${a.code})`}
      run={async (reason) =>
        void (await api.post(`/catalogue/assets/${a.code}`, {
          listed,
          depositEnabled: listed && dep,
          withdrawEnabled: listed && wd,
          buyEnabled: listed && buy,
          sellEnabled: listed && sell,
          convertEnabled: listed && conv,
          isNew,
          sortOrder: a.sortOrder,
          unavailableLabelFa: label || null,
          expectedVersion: a.version,
          reason,
        }))
      }
      onClose={onClose}
      onDone={onDone}
      data-asset-dialog={a.code}
    >
      <Facts rows={[['دقت دفتر کل (غیرقابل‌تغییر)', `${a.scale} رقم اعشار`]]} />
      <Check label="فهرست‌شده (قابل مشاهده برای کاربران)" v={listed} on={setListed} data="listed" />
      <Check label="واریز فعال" v={dep} on={setDep} data="deposit" />
      <Check label="برداشت فعال" v={wd} on={setWd} data="withdraw" />
      <Check label="خرید با تومان فعال" v={buy} on={setBuy} data="buy" />
      <Check label="فروش به تومان فعال" v={sell} on={setSell} data="sell" />
      <Check
        label={a.code === 'USDT' ? 'تبدیل ارزها با تتر فعال' : 'تبدیل با تتر فعال'}
        v={conv}
        on={setConv}
        data="convert"
      />
      <Check label="نشان «جدید»" v={isNew} on={setIsNew} />
      <TextField label="برچسب «موقتاً در دسترس نیست» (اختیاری)" value={label} onChange={setLabel} />
    </CommandDialog>
  );
}

function NetworkNewDialog({
  a,
  onClose,
  onDone,
}: {
  a: AssetRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [f, setF] = useState({
    code: '',
    nameFa: '',
    nameEn: '',
    family: 'TRON',
    addressRegex: '',
    memoRequired: false,
    memoLabelFa: '',
    memoRegex: '',
    explorerTx: '',
    explorerAddress: '',
  });
  const set = (k: keyof typeof f) => (v: string | boolean) => setF((x) => ({ ...x, [k]: v }));
  return (
    <CommandDialog
      title={`شبکه‌ی جدید برای ${a.code}`}
      canSubmit={
        /^[A-Z0-9_]{2,20}$/.test(f.code) &&
        f.nameFa.length >= 2 &&
        f.nameEn.length >= 2 &&
        (f.family !== 'REGEX' || !!f.addressRegex)
      }
      run={async (reason) =>
        void (await api.post('/catalogue/networks', {
          asset: a.code,
          code: f.code,
          nameFa: f.nameFa,
          nameEn: f.nameEn,
          family: f.family,
          addressRegex: f.addressRegex || null,
          memoRequired: f.memoRequired,
          memoLabelFa: f.memoLabelFa || null,
          memoRegex: f.memoRegex || null,
          explorerTx: f.explorerTx || null,
          explorerAddress: f.explorerAddress || null,
          reason,
        }))
      }
      onClose={onClose}
      onDone={onDone}
      data-network-new
    >
      <p className="m-0 text-[12.5px] text-fg-muted">
        کد، خانواده‌ی اعتبارسنجی و قاعده‌ی ممو پس از ثبت تغییر نمی‌کنند. شبکه با واریز و برداشت
        غیرفعال ساخته می‌شود.
      </p>
      <div className="grid gap-2 sm:grid-cols-3">
        <TextField
          label="کد (مثلاً TRC20)"
          value={f.code}
          onChange={(v) => set('code')(v.toUpperCase())}
          ltr
        />
        <TextField label="نام فارسی" value={f.nameFa} onChange={set('nameFa')} />
        <TextField label="نام انگلیسی" value={f.nameEn} onChange={set('nameEn')} ltr />
      </div>
      <SelectField
        label="خانواده‌ی اعتبارسنجی آدرس"
        value={f.family}
        onChange={set('family')}
        options={['EVM', 'TRON', 'BTC', 'SOLANA', 'REGEX'].map((x) => ({ value: x, label: x }))}
      />
      {f.family === 'REGEX' && (
        <TextField
          label="الگوی آدرس (Regex)"
          value={f.addressRegex}
          onChange={set('addressRegex')}
          ltr
        />
      )}
      <Check label="ممو / تگ الزامی" v={f.memoRequired} on={set('memoRequired')} />
      {f.memoRequired && (
        <div className="grid gap-2 sm:grid-cols-2">
          <TextField
            label="نام ممو (مثلاً تگ)"
            value={f.memoLabelFa}
            onChange={set('memoLabelFa')}
          />
          <TextField
            label="الگوی ممو (اختیاری)"
            value={f.memoRegex}
            onChange={set('memoRegex')}
            ltr
          />
        </div>
      )}
      <TextField
        label="الگوی لینک تراکنش (https://…{txid})"
        value={f.explorerTx}
        onChange={set('explorerTx')}
        ltr
      />
      <TextField
        label="الگوی لینک آدرس (https://…{address})"
        value={f.explorerAddress}
        onChange={set('explorerAddress')}
        ltr
      />
    </CommandDialog>
  );
}

function NetworkDialog({
  n,
  onClose,
  onDone,
}: {
  n: Net;
  onClose: () => void;
  onDone: () => void;
}) {
  const [dep, setDep] = useState(n.depositEnabled);
  const [wd, setWd] = useState(n.withdrawEnabled);
  const [label, setLabel] = useState(n.unavailableLabelFa ?? '');
  const [tx, setTx] = useState(n.explorerTx ?? '');
  return (
    <CommandDialog
      title={`شبکه‌ی ${n.nameFa}`}
      run={async (reason) =>
        void (await api.post(`/catalogue/networks/${n.id}`, {
          depositEnabled: dep,
          withdrawEnabled: wd,
          nameFa: n.nameFa,
          unavailableLabelFa: label || null,
          explorerTx: tx || null,
          explorerAddress: n.explorerAddress,
          sortOrder: n.sortOrder,
          expectedVersion: n.version,
          reason,
        }))
      }
      onClose={onClose}
      onDone={onDone}
      data-network-dialog={n.code}
    >
      <Check label="واریز فعال" v={dep} on={setDep} data="net-deposit" />
      <Check label="برداشت فعال (نیازمند سیاست کارمزد)" v={wd} on={setWd} data="net-withdraw" />
      <TextField label="برچسب «موقتاً در دسترس نیست»" value={label} onChange={setLabel} />
      <TextField label="الگوی لینک تراکنش" value={tx} onChange={setTx} ltr />
    </CommandDialog>
  );
}

function FeeDialog({
  a,
  n,
  onClose,
  onDone,
}: {
  a: AssetRow;
  n: Net;
  onClose: () => void;
  onDone: () => void;
}) {
  const [fee, setFee] = useState(n.fee?.fee ?? '');
  const [min, setMin] = useState(n.fee?.min ?? '');
  return (
    <CommandDialog
      title={`کارمزد و حداقل برداشت — ${n.nameFa}`}
      canSubmit={/^\d+(\.\d+)?$/.test(fee) && /^\d+(\.\d+)?$/.test(min)}
      run={async (reason) =>
        void (await api.post(`/catalogue/networks/${n.id}/fee`, { fee, min, reason }))
      }
      onClose={onClose}
      onDone={onDone}
      data-fee-dialog={n.code}
    >
      <p className="m-0 text-[12.5px] text-fg-muted">
        نسخه‌ی تازه‌ای از سیاست ثبت می‌شود (بازنویسی دستی). برداشت‌های ثبت‌شده با کارمزد زمان خودشان
        انجام می‌شوند.
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        <TextField label={`کارمزد شبکه (${a.code})`} value={fee} onChange={setFee} ltr />
        <TextField label={`حداقل مقدار مقصد (${a.code})`} value={min} onChange={setMin} ltr />
      </div>
    </CommandDialog>
  );
}

function AddressDialog({
  n,
  onClose,
  onDone,
}: {
  n: Net;
  onClose: () => void;
  onDone: () => void;
}) {
  const [address, setAddress] = useState('');
  const [label, setLabel] = useState('');
  return (
    <CommandDialog
      title={`آدرس واریز اشتراکی — ${n.nameFa}`}
      canSubmit={address.trim().length >= 8 && label.trim().length >= 2}
      run={async (reason) =>
        void (await api.post(`/catalogue/networks/${n.id}/addresses`, {
          address: address.trim(),
          label,
          reason,
        }))
      }
      onClose={onClose}
      onDone={onDone}
      data-address-dialog={n.code}
    >
      <p className="m-0 text-[12.5px] text-fg-muted">
        آدرس با قواعد شبکه اعتبارسنجی می‌شود و پس از ثبت تغییر نمی‌کند.
      </p>
      <TextField label="آدرس" value={address} onChange={setAddress} ltr />
      <TextField label="برچسب داخلی" value={label} onChange={setLabel} />
    </CommandDialog>
  );
}

interface CatalogueListRow {
  id: string;
  decision: string;
  criteria: string;
  assets: number;
  existing: number;
  listed: number;
}

/**
 * Owner-approved coin lists that ship with the code (OD-43). Import = create missing assets (inactive),
 * map the read-only market symbols, list them with the chosen routes. Deposit / withdrawal of new
 * assets stay off. One command: step-up + reason + audit.
 */
function ApprovedLists({ onImported }: { onImported: () => void }) {
  const r = useRemote<{ lists: CatalogueListRow[] }>('/catalogue/lists');
  const [open, setOpen] = useState<CatalogueListRow | null>(null);
  const [f, setF] = useState({ buy: true, sell: true, convert: true });
  return (
    <Card className="mb-4 flex flex-col gap-3 p-5" data-catalogue-lists>
      <h2 className="m-0 text-[15px] font-bold">فهرست‌های تأییدشده‌ی ارز</h2>
      <Remote r={r}>
        {(d) =>
          d.lists.length === 0 ? (
            <StateView compact kind="empty" title="فهرست تأییدشده‌ای موجود نیست" />
          ) : (
            <ul className="m-0 grid list-none gap-2 p-0 text-[13px]">
              {d.lists.map((l) => (
                <li
                  key={l.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line px-3 py-2"
                  data-catalogue-list={l.id}
                >
                  <span className="flex flex-col gap-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <bdi dir="ltr" className="font-mono text-[12.5px]">
                        {l.id}
                      </bdi>
                      <Badge tone="neutral">{l.decision}</Badge>
                    </span>
                    <span className="text-fg-muted">
                      {l.assets.toLocaleString('fa-IR')} ارز · تعریف‌شده{' '}
                      {l.existing.toLocaleString('fa-IR')} · فهرست‌شده{' '}
                      {l.listed.toLocaleString('fa-IR')}
                    </span>
                  </span>
                  <Button
                    size="sm"
                    variant="secondary"
                    onPress={() => setOpen(l)}
                    data-catalogue-import={l.id}
                  >
                    وارد کردن و فهرست‌سازی
                  </Button>
                </li>
              ))}
            </ul>
          )
        }
      </Remote>
      {open && (
        <CommandDialog
          title={`وارد کردن فهرست ${open.id}`}
          tone="caution"
          run={async (reason) =>
            void (await api.post(`/catalogue/lists/${open.id}/import`, {
              listed: true,
              buyEnabled: f.buy,
              sellEnabled: f.sell,
              convertEnabled: f.convert,
              reason,
            }))
          }
          onClose={() => setOpen(null)}
          onDone={() => (setOpen(null), r.reload(), onImported())}
        >
          <Facts
            rows={[
              ['ارزها', open.assets.toLocaleString('fa-IR')],
              ['معیار', open.criteria],
              ['واریز و برداشت', 'برای ارزهای تازه غیرفعال می‌ماند'],
            ]}
          />
          {(
            [
              ['buy', 'خرید با تومان'],
              ['sell', 'فروش به تومان'],
              ['convert', 'تبدیل با تتر'],
            ] as const
          ).map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-[13px]">
              <input
                type="checkbox"
                checked={f[k]}
                onChange={(e) => setF({ ...f, [k]: e.target.checked })}
              />
              {label}
            </label>
          ))}
        </CommandDialog>
      )}
    </Card>
  );
}

export function CataloguePage() {
  const r = useRemote<{ assets: AssetRow[] }>('/catalogue');
  const [open, setOpen] = useState<Open | null>(null);
  const done = () => (setOpen(null), r.reload());
  return (
    <AppContainer>
      <PageHeader title="دارایی‌ها و شبکه‌ها" />
      <ApprovedLists onImported={() => r.reload()} />
      <Remote r={r}>
        {(d) =>
          d.assets.length === 0 ? (
            <Card className="p-6">
              <StateView kind="empty" title="دارایی رمزارزی تعریف نشده است" />
            </Card>
          ) : (
            <div className="flex flex-col gap-4" data-catalogue>
              {d.assets.map((a) => (
                <Card
                  key={a.code}
                  className="flex flex-col gap-3 p-5"
                  data-catalogue-asset={a.code}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h2 className="m-0 flex items-center gap-2 text-[16px] font-bold">
                      {a.nameFa}{' '}
                      <bdi dir="ltr" className="font-mono text-[13px] text-fg-muted">
                        {a.code}
                      </bdi>
                      <Badge tone={a.listed ? 'positive' : 'neutral'}>
                        {a.listed ? 'فهرست‌شده' : 'فهرست‌نشده'}
                      </Badge>
                      {a.listed && (
                        <Badge tone={a.depositEnabled ? 'positive' : 'neutral'}>
                          واریز {a.depositEnabled ? 'فعال' : 'غیرفعال'}
                        </Badge>
                      )}
                      {a.listed && (
                        <Badge tone={a.withdrawEnabled ? 'positive' : 'neutral'}>
                          برداشت {a.withdrawEnabled ? 'فعال' : 'غیرفعال'}
                        </Badge>
                      )}
                      {a.listed && (
                        <Badge tone={a.buyEnabled && a.sellEnabled ? 'positive' : 'neutral'}>
                          خرید/فروش{' '}
                          {a.buyEnabled && a.sellEnabled
                            ? 'فعال'
                            : a.buyEnabled || a.sellEnabled
                              ? 'نیمه‌فعال'
                              : 'غیرفعال'}
                        </Badge>
                      )}
                      {a.listed && (
                        <Badge tone={a.convertEnabled ? 'positive' : 'neutral'}>
                          تبدیل {a.convertEnabled ? 'فعال' : 'غیرفعال'}
                        </Badge>
                      )}
                    </h2>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="secondary"
                        onPress={() => setOpen({ k: 'asset', a })}
                        data-asset-edit={a.code}
                      >
                        فهرست‌سازی و دسترس‌پذیری
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onPress={() => setOpen({ k: 'net-new', a })}
                      >
                        شبکه‌ی جدید
                      </Button>
                    </div>
                  </div>
                  {a.networks.length === 0 ? (
                    <p className="m-0 text-[13px] text-fg-muted">شبکه‌ای تعریف نشده است.</p>
                  ) : (
                    <ul className="m-0 grid list-none gap-2 p-0">
                      {a.networks.map((n) => (
                        <li
                          key={n.id}
                          className="rounded-md border border-line bg-surface-1 p-3"
                          data-catalogue-network={n.code}
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="flex flex-wrap items-center gap-2 text-[13.5px] font-semibold">
                              {n.nameFa}{' '}
                              <bdi dir="ltr" className="font-mono text-[12px] text-fg-muted">
                                {n.code} · {n.family}
                              </bdi>
                              {n.memoRequired && <Badge>{n.memoLabelFa ?? 'ممو'} الزامی</Badge>}
                              <Badge tone={n.depositEnabled ? 'positive' : 'neutral'}>واریز</Badge>
                              <Badge tone={n.withdrawEnabled ? 'positive' : 'neutral'}>
                                برداشت
                              </Badge>
                            </span>
                            <span className="flex flex-wrap gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onPress={() => setOpen({ k: 'net', a, n })}
                              >
                                تنظیم
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onPress={() => setOpen({ k: 'fee', a, n })}
                              >
                                کارمزد
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onPress={() => setOpen({ k: 'addr', a, n })}
                              >
                                آدرس واریز
                              </Button>
                            </span>
                          </div>
                          <p className="m-0 mt-1.5 text-[12.5px] text-fg-muted">
                            {n.fee ? (
                              <>
                                کارمزد{' '}
                                <bdi dir="ltr" className="font-mono">
                                  {exact(n.fee.fee, a.code)}
                                </bdi>{' '}
                                · حداقل{' '}
                                <bdi dir="ltr" className="font-mono">
                                  {exact(n.fee.min, a.code)}
                                </bdi>{' '}
                                ·{' '}
                                {n.fee.source === 'ADMIN_OVERRIDE'
                                  ? 'بازنویسی دستی'
                                  : 'داده‌ی مرجع'}{' '}
                                · {dt(n.fee.createdAt)}
                              </>
                            ) : (
                              'سیاست کارمزد تعریف نشده — برداشت در این شبکه ممکن نیست.'
                            )}
                          </p>
                          {n.addresses.length > 0 && (
                            <ul className="m-0 mt-2 grid list-none gap-1 p-0 text-[12.5px]">
                              {n.addresses.map((x) => (
                                <li key={x.id} className="flex flex-wrap items-center gap-2">
                                  <LongId value={x.address} />
                                  <span className="text-fg-muted">{x.label}</span>
                                  <Badge tone={x.status === 'ACTIVE' ? 'positive' : 'neutral'}>
                                    {x.status === 'ACTIVE' ? 'فعال' : 'بازنشسته'}
                                  </Badge>
                                  <span className="text-fg-subtle">
                                    {x.assignedUsers.toLocaleString('fa-IR')} کاربر
                                  </span>
                                  {x.status === 'ACTIVE' && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onPress={() =>
                                        setOpen({
                                          k: 'retire',
                                          a,
                                          n,
                                          id: x.id,
                                          version: x.version,
                                          address: x.address,
                                        })
                                      }
                                    >
                                      بازنشسته
                                    </Button>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              ))}
            </div>
          )
        }
      </Remote>
      {open?.k === 'asset' && (
        <AssetDialog a={open.a} onClose={() => setOpen(null)} onDone={done} />
      )}
      {open?.k === 'net-new' && (
        <NetworkNewDialog a={open.a} onClose={() => setOpen(null)} onDone={done} />
      )}
      {open?.k === 'net' && (
        <NetworkDialog n={open.n} onClose={() => setOpen(null)} onDone={done} />
      )}
      {open?.k === 'fee' && (
        <FeeDialog a={open.a} n={open.n} onClose={() => setOpen(null)} onDone={done} />
      )}
      {open?.k === 'addr' && (
        <AddressDialog n={open.n} onClose={() => setOpen(null)} onDone={done} />
      )}
      {open?.k === 'retire' && (
        <CommandDialog
          title="بازنشسته کردن آدرس واریز"
          tone="caution"
          run={async (reason) =>
            void (await api.post(`/catalogue/addresses/${open.id}/retire`, {
              expectedVersion: open.version,
              reason,
            }))
          }
          onClose={() => setOpen(null)}
          onDone={done}
        >
          <Facts
            rows={[
              ['آدرس', <LongId key="a" value={open.address} />],
              ['اثر', 'کاربرانِ این آدرس در درخواست بعدی آدرس تازه می‌گیرند؛ سوابق حفظ می‌شود.'],
            ]}
          />
        </CommandDialog>
      )}
    </AppContainer>
  );
}
